import { useState } from "react";

import { Banner } from "@/components/ui/banner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAdjustSeats } from "@/hooks/queries/useAdjustSeats";
import {
  capacityBelowConsumedMessage,
  SEAT_CAPACITY_UNCHANGED_MESSAGE,
  toAdminOrgError,
  type AdminOrgBanner,
} from "@/routes/admin/admin-orgs-error";
import {
  SEAT_CAPACITY_MAX,
  SEAT_CAPACITY_MIN,
} from "@/routes/admin/create-organization-schema";

// Story 15.2 — the Contract card's `seatControl` slot on Screen 24.
//
// A DIALOG behind the mockup's one `btn soft` trigger, not an inline field on the
// card: the card's own Save/Cancel is 15.6's `update-details` write, and a seat
// field rendered beside it would put a SECOND endpoint — with its own capability
// and its own refusals — behind one Save button.
//
// ONE ABSOLUTE INPUT, and no `+1` / `-1` step buttons. `AdjustSeatsRequest`
// documents `seat_capacity` as "absolute value, not a delta"
// (`adjust_seats/schemas.py:16`), and a step button issues one POST plus one
// 7-year audit row per click against a base this screen may be holding stale.
//
// NO PRORATION, and nothing here computes one (FR48/FR59/NFR40/AR54). A capacity
// change is one column UPDATE and one audit row: no billing call, no second POST,
// no credit, no refund, no invoice. The card body already carries the
// "no rollover, no credits, no proration" sentence once, so this slot does not
// restate it.
//
// NO ROLE BRANCH (AC12). `/admin/orgs` is fenced twice already — by
// `route-access.ts:67` and by the `SUPER_ADMIN_ROLES` `RoleGuard`
// (`app-routes.tsx:168`, wrapping the org routes at `:184-186`) — and the
// backend capability decides the request. A
// third gate here would be a fourth place to forget, and the CI RBAC grep-gate
// scans `src/application` only, so it would fail no gate.

export const ADJUST_SEAT_CAPACITY_TRIGGER_LABEL = "Adjust seat capacity";
export const ADJUST_SEAT_CAPACITY_SUBMIT_LABEL = "Save seat capacity";
export const ADJUST_SEAT_CAPACITY_PENDING_LABEL = "Saving\u2026";

/**
 * Client-side mirror of the SERVER's own bounds (AC6). The two constants are
 * imported from the create-organization form's schema, never re-declared: one
 * server bound with two client sources is one that drifts.
 */
export const SEAT_CAPACITY_WHOLE_NUMBER_MESSAGE = "Enter a whole number of seats.";
export const SEAT_CAPACITY_MIN_MESSAGE = `Enter a seat capacity of ${SEAT_CAPACITY_MIN} or more.`;
export const SEAT_CAPACITY_MAX_MESSAGE = "That seat capacity is too large.";

// `SEAT_CAPACITY_UNCHANGED_MESSAGE` moved to `admin-orgs-error.ts` (morning
// triage, 2026-09-05) when 15.11's ORG-020 branch landed there. The server now
// raises the SAME refusal this control's `validate()` raises, so the sentence
// belongs beside the mapper that renders the server's half — the layering
// `capacityBelowConsumedMessage` already uses. Importing it back the other way
// would put a component in the mapper's import graph and close a cycle.

/**
 * AC7 — the client refuses STRICTLY BELOW the consumed count and allows equality.
 *
 * The guard is `<`, never `<=`: `adjust_seats/command.py :: execute_adjust_seats`
 * refuses only strictly-below (the `seat_capacity < org.seat_consumed` arm that
 * raises `CapacityBelowConsumedException`), and
 * `test_decrease_to_exactly_consumed_is_allowed` pins
 * `seat_capacity == seat_consumed` (which drives `remaining` to `0`) as legal. A
 * `<=` here would refuse a value the product accepts, and no server test catches
 * a client that is too strict.
 *
 * It is SAFE to guard client-side only because FR83 makes `seat_consumed` one-way
 * within a term: the rendered figure can be stale-LOW but never stale-high, so
 * this guard can only ever refuse something the server would also refuse. The
 * ORG-007 branch stays regardless — that is the stale-LOW case, and the two do
 * not substitute for each other.
 *
 * The SENTENCE is the mapper's, imported: the stale-LOW case renders the server
 * -side copy immediately after this one, and two wordings for one rule would
 * drift in front of the operator who saw both.
 */
function belowConsumedMessage(consumed: number): string {
  return capacityBelowConsumedMessage(String(consumed));
}

const TABULAR = "[font-variant-numeric:tabular-nums]";

function SeatFigure({ label, value }: { label: string; value: number }) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-sm text-[color:var(--text-tertiary)]">{label}</span>
      <span className={`text-md font-bold ${TABULAR}`}>{value}</span>
    </div>
  );
}

/**
 * Validate the typed capacity against every bound, in one place, at SUBMIT.
 *
 * Not per keystroke: an operator clearing the field to retype is not making a
 * mistake, and the `ChangeEmailDialog` precedent checks on confirm for the same
 * reason. Returns the message to show, or `null` when the value may be sent.
 */
function validate(
  raw: string,
  { seatCapacity, seatConsumed }: { seatCapacity: number; seatConsumed: number },
): string | null {
  const trimmed = raw.trim();
  if (trimmed.length === 0) return SEAT_CAPACITY_MIN_MESSAGE;
  // Parsed off the STRING the control holds, not through `Number()` alone:
  // `Number("3.5")` and `Number(" 3 ")` both survive a `>= 1` check.
  if (!/^-?\d+$/.test(trimmed)) return SEAT_CAPACITY_WHOLE_NUMBER_MESSAGE;

  const value = Number(trimmed);
  if (value < SEAT_CAPACITY_MIN) return SEAT_CAPACITY_MIN_MESSAGE;
  if (value > SEAT_CAPACITY_MAX) return SEAT_CAPACITY_MAX_MESSAGE;
  if (value < seatConsumed) return belowConsumedMessage(seatConsumed);
  // Story 15.11 reversed the wire: a no-op POST is now REFUSED there too, with
  // 409 ORG-020 (`test_setting_the_capacity_to_its_current_value_is_refused`).
  // This guard is no longer the only thing standing between the operator and an
  // `old == new` row in an append-only 7-year store — it is the cheaper half of
  // the same rule, and it still earns its place because it refuses without a
  // round trip. The stale-card case still reaches the server's ORG-020, which
  // the mapper renders with THIS sentence.
  if (value === seatCapacity) return SEAT_CAPACITY_UNCHANGED_MESSAGE;
  return null;
}

export function AdjustSeatCapacityControl({
  organizationId,
  seatCapacity,
  seatConsumed,
}: {
  organizationId: string;
  seatCapacity: number;
  seatConsumed: number;
}) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(String(seatCapacity));
  const [validationError, setValidationError] = useState<string | null>(null);
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [banner, setBanner] = useState<AdminOrgBanner | null>(null);
  const adjustSeats = useAdjustSeats(organizationId);

  function openDialog() {
    // Re-seeded from the CURRENT capacity every time the dialog opens, so a
    // cancelled edit never becomes the next edit's starting point.
    setDraft(String(seatCapacity));
    setValidationError(null);
    setFieldError(null);
    setBanner(null);
    setOpen(true);
  }

  function onSubmit() {
    // AC10 — the re-entrancy guard. `disabled` alone loses the race on a
    // double-click that lands before React re-renders.
    if (adjustSeats.isPending) return;

    const message = validate(draft, { seatCapacity, seatConsumed });
    if (message !== null) {
      setValidationError(message);
      setFieldError(null);
      setBanner(null);
      return;
    }

    setValidationError(null);
    setFieldError(null);
    setBanner(null);
    adjustSeats.mutate(
      { seat_capacity: Number(draft.trim()) },
      {
        onSuccess: () => setOpen(false),
        onError: (error) => {
          // Keyed on the CODE through the shared mapper — never on the server's
          // English prose, and never by parsing the consumed count out of it.
          // The typed value is deliberately left in the input: the operator has
          // to see which number they are changing.
          const mapped = toAdminOrgError(error);
          setFieldError(mapped.fieldErrors.seatCapacity ?? null);
          setBanner(mapped.banner);
        },
      },
    );
  }

  const inlineError = validationError ?? fieldError;
  const remaining = seatCapacity - seatConsumed;

  return (
    <>
      <Button variant="secondary" size="sm" onClick={openDialog}>
        {ADJUST_SEAT_CAPACITY_TRIGGER_LABEL}
      </Button>

      <Dialog open={open} onOpenChange={(next) => (next ? openDialog() : setOpen(false))}>
        <DialogContent className="w-[min(var(--width-md,520px),100%)]">
          <DialogHeader>
            <DialogTitle>{ADJUST_SEAT_CAPACITY_TRIGGER_LABEL}</DialogTitle>
            <DialogDescription>
              Set the total number of seats on this contract. The new value replaces the
              current one — it is not added to it.
            </DialogDescription>
          </DialogHeader>

          {/*
            TWO figures, and the labels are the shipped FR83 ones — `Consumed` /
            `Remaining`, not Screen 24's pre-FR83 "Assigned" / "Available"
            (`OrgAdminDashboardScreen.tsx:258-263` made the same reconciliation).
            The current capacity is NOT a third tile here: the card already
            carries that stat and the input opens holding it.
          */}
          <div className="flex gap-6">
            <SeatFigure label="Consumed" value={seatConsumed} />
            <SeatFigure label="Remaining" value={remaining} />
          </div>

          <div className="flex flex-col gap-1">
            <Label htmlFor="adjust-seat-capacity-input">New seat capacity</Label>
            <Input
              id="adjust-seat-capacity-input"
              // `inputMode` rather than `type="number"`: the spinner a number
              // input grows IS a step control, which AC2 refuses.
              inputMode="numeric"
              autoComplete="off"
              className={TABULAR}
              value={draft}
              aria-invalid={inlineError ? true : undefined}
              aria-describedby={inlineError ? "adjust-seat-capacity-error" : undefined}
              onChange={(event) => {
                setDraft(event.target.value);
                setValidationError(null);
                setFieldError(null);
              }}
            />
            {inlineError ? (
              <p
                id="adjust-seat-capacity-error"
                role="alert"
                className="text-sm text-[color:var(--text-error)]"
              >
                {inlineError}
              </p>
            ) : null}
          </div>

          {banner ? (
            <Banner variant={banner.variant} title={banner.title}>
              <p>{banner.body}</p>
              {banner.code ? (
                <p className="text-sm">{`Reference code: ${banner.code}`}</p>
              ) : null}
            </Banner>
          ) : null}

          <DialogFooter>
            <Button variant="secondary" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button onClick={onSubmit} disabled={adjustSeats.isPending}>
              {adjustSeats.isPending
                ? ADJUST_SEAT_CAPACITY_PENDING_LABEL
                : ADJUST_SEAT_CAPACITY_SUBMIT_LABEL}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
